// Components
import {
  Button,
  Group,
  Loader,
  ScrollArea,
  Stack,
  Tabs,
  Text,
} from "@mantine/core";
import { Drawer } from "@benchmark/ui/components/atomic";
import { IconBell } from "@tabler/icons-react";
import { ClinicalNotificationItem } from "./ClinicalNotificationItem";
import { ClinicalNotificationEmptyState } from "./ClinicalNotificationEmptyState";

// Styles
import styles from "./ClinicalNotificationDrawer.module.css";

// Hooks
import { useState } from "react";
import { useNavigate } from "react-router";
import { useMedplum } from "@medplum/react";
import {
  useNotificationTasks,
  type NotificationTab,
} from "@/hooks/domain/useNotificationTasks";

// Types
import type { Task } from "@medplum/fhirtypes";

// Facade
import { createNotificationTaskFacade } from "@/facades/notification-task.facade";

// Utils
import { showErrorNotification } from "@benchmark/ui/utils";

/**
 * Props for {@link ClinicalNotificationDrawer}.
 * @agentic connections
 */
export type ClinicalNotificationDrawerProps = {
  opened: boolean;
  onClose: () => void;
  /** Current user's PractitionerRole ids for the "mine" tab. */
  ownerRoleIds: string[];
  /** Reference of the acting PractitionerRole for the read trace (optional). */
  actingRoleReference?: string;
  actingRoleDisplay?: string;
};

/**
 * Right-side notification center with "Mine" and "Team queue" tabs. Shows only
 * clinical notification Tasks; marking read updates state without touching
 * operational Task surfaces.
 * @agentic connections
 */
export const ClinicalNotificationDrawer = ({
  opened,
  onClose,
  ownerRoleIds,
  actingRoleReference,
  actingRoleDisplay,
}: ClinicalNotificationDrawerProps) => {
  const medplum = useMedplum();
  const navigate = useNavigate();
  const [tab, setTab] = useState<NotificationTab>("mine");
  const [pendingId, setPendingId] = useState<string | null>(null);

  const { tasks, loading, error, hasMore, loadMore, refetch } =
    useNotificationTasks({
      tab,
      ownerRoleIds,
      enabled: opened,
    });

  const handleOpenPatient = (patientId: string) => {
    onClose();
    navigate(`/Patient/${patientId}`);
  };

  const handleMarkRead = async (task: Task) => {
    if (!task.id) return;
    setPendingId(task.id);
    try {
      await createNotificationTaskFacade(medplum).markRead({
        task: task as Task & { id: string },
        readByReference: actingRoleReference,
        readByDisplay: actingRoleDisplay,
      });
      refetch();
    } catch (err) {
      showErrorNotification(err, { title: "Could not mark notification read" });
    } finally {
      setPendingId(null);
    }
  };

  return (
    <Drawer
      opened={opened}
      onClose={onClose}
      size="md"
      colorScheme="dark"
      icon={<IconBell size={18} aria-hidden />}
      title="Notifications"
      padding={0}
    >
      <Tabs
        value={tab}
        onChange={(value) => setTab(value as NotificationTab)}
        classNames={{ list: styles.tabsList, tab: styles.tab }}
        style={{ display: "flex", flexDirection: "column", height: "100%" }}
      >
        <Tabs.List px="md" pt="md">
          <Tabs.Tab value="mine">Mine</Tabs.Tab>
          <Tabs.Tab value="queue">Team queue</Tabs.Tab>
        </Tabs.List>

        <Tabs.Panel value={tab} style={{ flex: 1, minHeight: 0 }}>
          {loading && tasks.length === 0 ? (
            <Group justify="center" py="xl">
              <Loader size="sm" color="purple.3" />
            </Group>
          ) : error && tasks.length === 0 ? (
            <ClinicalNotificationEmptyState message="Could not load notifications. Try again shortly." />
          ) : tasks.length === 0 ? (
            <ClinicalNotificationEmptyState
              message={
                tab === "queue"
                  ? "No unassigned notifications"
                  : "No notifications"
              }
            />
          ) : (
            <ScrollArea h="100%" p="md" onBottomReached={loadMore}>
              <Stack gap="sm">
                {error && (
                  <Text size="xs" c="red.3" role="status">
                    Could not refresh notifications. Showing the last loaded
                    list.
                  </Text>
                )}
                {tasks.map((task) => (
                  <ClinicalNotificationItem
                    key={task.id}
                    task={task}
                    onOpenPatient={handleOpenPatient}
                    onMarkRead={handleMarkRead}
                    markReadPending={pendingId === task.id}
                  />
                ))}
                {/* Scrolling to the bottom loads more on its own; the button covers
                    keyboard users and a first page too short to scroll. */}
                {hasMore && (
                  <Button
                    variant="subtle"
                    size="xs"
                    loading={loading}
                    onClick={loadMore}
                    styles={{ root: { color: "rgba(255,255,255,0.75)" } }}
                  >
                    Show older notifications
                  </Button>
                )}
              </Stack>
            </ScrollArea>
          )}
        </Tabs.Panel>
      </Tabs>
    </Drawer>
  );
};
